import React, { useMemo, useState } from "react";
import {
	AntdButton,
	Container,
	DetailCard,
	DetailCardHeader,
	DetailList,
	DetailListItem,
	Inner,
	QuotationDetails,
	QuotationInfo,
	Section,
	TextEllipsis,
} from "./styles";
import Image from "../image/Image";
import { COLORS, Typo } from "../../layout/theme";
import { numberWithDotAndCommas } from "../../../utils/numberWithDotAndComas";
import { BiChevronDown } from "react-icons/bi";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	SafetyCertificateOutlined,
	ToolOutlined,
	InfoCircleOutlined,
	UnorderedListOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";

// Íconos y colores por categoría de detalle, para dar identidad visual a cada
// bloque dentro del panel de "Detalles" de la cotización.
const DETAIL_CATEGORY_STYLE = {
	"Coberturas Principales": {
		icon: SafetyCertificateOutlined,
		color: COLORS.Secondary,
	},
	"Servicios Adicionales": {
		icon: ToolOutlined,
		color: COLORS.Warning,
	},
	"Información del Plan": {
		icon: InfoCircleOutlined,
		color: COLORS.Primary,
	},
	default: {
		icon: UnorderedListOutlined,
		color: COLORS.MediumGrey,
	},
};

/**
 * Parsea un string HTML simple con formato "<ul><li>Item 1</li><li>Item 2</li></ul>"
 * (tal como lo genera el backend a partir del campo "detail" de cada plan) y
 * devuelve un array de strings con el texto de cada item, ya "unescapado".
 *
 * No se utiliza dangerouslySetInnerHTML ni DOMParser para evitar riesgos de
 * seguridad y mantener el renderizado 100% controlado por React.
 */
const parseDetailHtmlToItems = (detailHtml) => {
	if (!detailHtml || typeof detailHtml !== "string") return [];

	const matches = [...detailHtml.matchAll(/<li>([\s\S]*?)<\/li>/g)];

	const unescapeHtml = (str) =>
		str
			.replace(/&amp;/g, "&")
			.replace(/&lt;/g, "<")
			.replace(/&gt;/g, ">")
			.replace(/&quot;/g, '"')
			.replace(/&#39;/g, "'");

	return matches.map((match) => unescapeHtml(match[1].trim())).filter(Boolean);
};

export default function QuotationCardList({
	item,
	handleConfirmQuotation,
	isMobile,
}) {
	const [toggleDetails, setToggleDetails] = useState(false);

	// Precio final a mostrar: item.price ya es el "precio_efectivo" que
	// viene del backend (multi-quote), el cual YA tiene el descuento
	// aplicado. NO debe volver a restarse el % de descuento acá, o se
	// estaría aplicando el descuento dos veces.
	const finalPrice = item.price;
	// Precio "de lista" (tachado): calculado en el backend por el motor de
	// descuentos (b2c-multi-quote/src/enrichers/discount_engine.py) y
	// expuesto como item.listPrice. Se mantiene un fallback local por si
	// el backend no lo provee, para no romper la UI.
	const listPrice = item.listPrice ?? Math.round(finalPrice * 1.2);



	// Detalle completo de coberturas provisto por la aseguradora (HTML generado
	// por el backend), parseado a una lista simple de items de texto.
	const fullDetailItems = useMemo(
		() => parseDetailHtmlToItems(item.detailHtml),
		[item.detailHtml]
	);

	return (
		<Inner>
			<QuotationInfo isMobile={isMobile}>
				<Container>
					<Image width={isMobile ? 80 : 100} src={item.companyLogo} />
				</Container>
				<Container>
					<Typo level={12} type="mediumGrey">
						SUMA ASEGURADA
					</Typo>
					<Typo level={10}>
						$ {numberWithDotAndCommas(item.valor_asegurado || 0)}
					</Typo>
				</Container>
				<Container>
					<Typo level={12} type="mediumGrey">
						PLAN
					</Typo>
					<Tooltip title={item.coverage}>
						<TextEllipsis level={10}>{item.coverage}</TextEllipsis>
					</Tooltip>
				</Container>
				<Container>
					<Typo level={12} type="mediumGrey">
						PRECIO
					</Typo>
					<Typo level={4}>
						$ {numberWithDotAndCommas(finalPrice)}
						<Typo level={10}>/mes</Typo>
					</Typo>
					{item.discount > 0 && (
						<Typo level={6} type="mediumGrey">
							<del>$ {numberWithDotAndCommas(listPrice)}</del>
						</Typo>
					)}
				</Container>
				<Container>
					<Typo level={12} type="mediumGrey">
						CRISTALES
					</Typo>
					{item.hasCrystal ? (
						<CheckCircleOutlined style={{ color: COLORS.Success }} />
					) : (
						<CloseCircleOutlined style={{ color: COLORS.Danger }} />
					)}
				</Container>
				<Container>
					<Typo level={12} type="mediumGrey">
						GRANIZO
					</Typo>
					{item.hasHail ? (
						<CheckCircleOutlined style={{ color: COLORS.Success }} />
					) : (
						<CloseCircleOutlined style={{ color: COLORS.Danger }} />
					)}
				</Container>

				<Container>
					<AntdButton
						key="NextStep2CarQuotation"
						type="primary"
						onClick={() => handleConfirmQuotation(item)}
					>
						<Typo level={5} type="white">
							Contratar
						</Typo>
					</AntdButton>
					<Section
						style={{ cursor: "pointer" }}
						onClick={() => setToggleDetails((prev) => !prev)}
					>
						<Typo level={10}>Detalles</Typo>
						<BiChevronDown
							size={20}
							color={COLORS.Secondary}
							style={{
								transform: toggleDetails ? "rotate(180deg)" : "rotate(0deg)",
								transition: "transform 0.2s ease-in-out",
							}}
						/>
					</Section>
				</Container>
			</QuotationInfo>
			{toggleDetails && (
				<QuotationDetails>
					{item.details.map((detail, idx) => {
						const categoryStyle =
							DETAIL_CATEGORY_STYLE[detail.title] ||
							DETAIL_CATEGORY_STYLE.default;
						const CategoryIcon = categoryStyle.icon;

						return (
							<DetailCard key={detail.title || idx}>
								<DetailCardHeader iconColor={categoryStyle.color}>
									<CategoryIcon
										style={{ fontSize: 18, color: categoryStyle.color }}
									/>
									<Typo level={12} type="mediumGrey">
										{detail.title}
									</Typo>
								</DetailCardHeader>
								<DetailList>
									{detail.items.map((i, itemIdx) => (
										<DetailListItem key={itemIdx}>
											<CheckCircleOutlined style={{ fontSize: 14 }} />
											<Typo level={10}>{i}</Typo>
										</DetailListItem>
									))}
								</DetailList>
							</DetailCard>
						);
					})}

					{fullDetailItems.length > 0 && (
						<DetailCard>
							<DetailCardHeader iconColor={DETAIL_CATEGORY_STYLE.default.color}>
								<UnorderedListOutlined
									style={{
										fontSize: 18,
										color: DETAIL_CATEGORY_STYLE.default.color,
									}}
								/>
								<Typo level={12} type="mediumGrey">
									Detalle Completo de Coberturas
								</Typo>
							</DetailCardHeader>
							<DetailList>
								{fullDetailItems.map((i, itemIdx) => (
									<DetailListItem key={itemIdx}>
										<CheckCircleOutlined style={{ fontSize: 14 }} />
										<Typo level={10}>{i}</Typo>
									</DetailListItem>
								))}
							</DetailList>
						</DetailCard>
					)}
				</QuotationDetails>
			)}
		</Inner>
	);
}
